import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  reportDeliveryMethodsService,
  CreateReportDeliveryMethodInput,
  UpdateReportDeliveryMethodInput,
} from "../../services/admin/reportdeliverymethods.service";

export function useReportDeliveryMethodsAdmin() {
  return useQuery({
    queryKey: ["admin", "report-delivery-methods"],
    queryFn: reportDeliveryMethodsService.getAll,
  });
}

export function useReportDeliveryMethod(id?: string) {
  return useQuery({
    queryKey: ["admin", "report-delivery-methods", id],
    queryFn: () => reportDeliveryMethodsService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateReportDeliveryMethodInput) =>
      reportDeliveryMethodsService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-delivery-methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["report-delivery-methods"] });
      notifications.show({
        title: "Success",
        message: "Report Delivery Method created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create report delivery method",
        color: "red",
      });
    },
  });
}

export function useUpdateReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateReportDeliveryMethodInput) =>
      reportDeliveryMethodsService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-delivery-methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["report-delivery-methods"] });
      notifications.show({
        title: "Success",
        message: "Report Delivery Method updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update report delivery method",
        color: "red",
      });
    },
  });
}

export function useDeleteReportDeliveryMethod() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => reportDeliveryMethodsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-delivery-methods"],
      });
      queryClient.invalidateQueries({ queryKey: ["report-delivery-methods"] });
      notifications.show({
        title: "Success",
        message: "Report Delivery Method deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete report delivery method",
        color: "red",
      });
    },
  });
}

export function useToggleReportDeliveryMethodActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      reportDeliveryMethodsService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "report-delivery-methods"],
      });
      queryClient.invalidateQueries({
        queryKey: ["report-delivery-methods"],
      });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
